import React, { FC, memo } from 'react';
import { DateTime } from 'luxon';
import styled from '@emotion/styled';

import { useButton, useI18n } from '~Hooks';
import { Button } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import Icon from '~ComponentsImproved/Icon';
import ArrowForwardIcon from '~Assets/icons/arrow_forward.svg';

import { AllUsers_allUsers } from '~Components/LinkUser/__queries__/AllUsers';
import MembershipLevelBadge from '~Components/LinkUser/SelectedUser/MembershipLevelBadge';

const Row = styled(Button)<{ isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  background-color: var(--colorBlueGray1100);
  border-radius: 8px;
  padding: 12px 16px;
  text-transform: none;
  min-height: 56px;
  box-sizing: border-box;
  align-items: center;
  position: relative;
  overflow: hidden;

  &:active {
    background-color: var(--colorBlueGray900);
  }

  border: ${({ isSelected }) =>
    isSelected ? '1px solid var(--colorBlueGray700)' : '1px solid var(--colorBlueGray1100)'};
`;

const RowDetails = styled.div`
  color: var(--colorWhite);
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: calc(100% - 30px);
`;

const RowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

interface Props {
  member: AllUsers_allUsers;
  isSelected: boolean;
  onSelect: () => void;
}

/** Individual member row - shows name, membership badge, and registration date */
const MemberRow: FC<Props> = ({ member, isSelected, onSelect }) => {
  const { i18n } = useI18n();

  const rowButton = useButton({
    onClick: onSelect,
  });

  // Fetch membership level from card or directly
  const membershipLevel = member.membershipCard?.membershipLevel ?? member.membershipLevel;

  return (
    <Row {...rowButton} isSelected={isSelected}>
      <RowDetails>
        {/* Name - always left aligned */}
        <Paragraph
          fontWeight={600}
          level={4}
          color="var(--colorWhite)"
          bottom={0}
          style={{ textAlign: 'left' }}>
          {member.name}
        </Paragraph>

        {/* Membership badge + date on the right */}
        <RowRight>
          {membershipLevel && (
            <MembershipLevelBadge
              title={i18n(membershipLevel.title)}
              primaryColor={membershipLevel.primaryColor || 'var(--colorBlueGray200)'}
            />
          )}

          <Paragraph level={5} color="var(--colorBlueGray200)" bottom={0}>
            {DateTime.fromISO(member.createdAt).toFormat('d MMM yyyy')}
          </Paragraph>
        </RowRight>
      </RowDetails>

      {isSelected && <Icon src={ArrowForwardIcon} color="var(--colorWhite)" height="24px" />}
    </Row>
  );
};

export default memo(MemberRow);
